import {CommentsCategory} from '@domain/comments'
import {Loading, ControlledCommentBox, H5, type ControlledCommentProps} from '@hm/fundamental'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  useAddComment,
  useEditComment,
  useDeleteComment
} from '@refra/modules/common/hooks/mutations'
import {useFundamentalComments} from '@refra/modules/common/hooks/queries'
import {logger} from '@refra/modules/common/utils'
import {MaterialInstallationDetails} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface InstallationCommentsProps {
  materialInstallationDetails: MaterialInstallationDetails
  isReadOnly?: boolean
}

export const InstallationComments: React.FC<InstallationCommentsProps> = ({
  materialInstallationDetails,
  isReadOnly = false
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')

  const [currentComment, setCurrentComment] = useState('')

  const comments = useFundamentalComments({
    commentsCategory: CommentsCategory.MaterialInstallations,
    commentCategoryInstanceIds: [materialInstallationDetails.id]
  })

  const {mutateAsync: addComment, isPending: isAddingComment} = useAddComment()
  const {mutateAsync: editComment, isPending: isEditingComment} = useEditComment()
  const {mutateAsync: deleteComment, isPending: isDeletingComment} = useDeleteComment()

  const handleAddComment = async () => {
    if (currentComment.trim().length === 0) return
    await addComment({
      plantCode,
      commentsCategory: CommentsCategory.MaterialInstallations,
      commentCategoryInstanceIds: materialInstallationDetails.id,
      comment: currentComment
    }).catch((e) => {
      logger.extras({details: e.message}).error('Adding material installation comment failed')
    })
    setCurrentComment('')
  }

  const handleEditComment = async (comment: ControlledCommentProps, value: string) => {
    if (value.length === 0) return
    await editComment({
      plantCode,
      commentsCategory: CommentsCategory.MaterialInstallations,
      commentCategoryInstanceIds: materialInstallationDetails.id,
      commentId: comment.id,
      comment: value
    }).catch((e) => {
      logger.extras({details: e.message}).error('Editing material installation comment failed')
    })
  }

  const handleDeleteComment = async (commentId: number) => {
    await deleteComment({
      plantCode,
      commentsCategory: CommentsCategory.MaterialInstallations,
      commentCategoryInstanceIds: materialInstallationDetails.id,
      commentId
    }).catch((e) => {
      logger.extras({details: e.message}).error('Deleting material installation comment failed')
    })
  }

  const commentsSectionTitle = `${t('common.comments')} (${comments?.length})`

  if (!comments) {
    return (
      <div className="flex justify-center">
        <Loading size="large" />
      </div>
    )
  }

  const areCommentsDisabled = isReadOnly || isAddingComment || isEditingComment || isDeletingComment

  return (
    <div className="flex flex-col gap-3">
      <H5 data-cy="material_modal_comments_title">{commentsSectionTitle}</H5>
      <ControlledCommentBox
        comments={comments}
        commentInputValue={currentComment}
        onCommentInputChange={(value) => setCurrentComment(value)}
        onSubmit={handleAddComment}
        onEdit={handleEditComment}
        onDelete={handleDeleteComment}
        disabled={areCommentsDisabled}
      />
    </div>
  )
}
